// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLoading } from 'redux/slices/loadingSlice';
import { createAxios } from 'ts/createAxios';
import { getApiPath, nonNull, normalizeNumber } from 'util/commons';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError } from 'util/errorHandler';
import { BookingQRScannedData } from 'util/Types';
import styles from './RecycleQRScanning.module.scss';
import BookingScannedTable from 'components/molecules/BookingScannedTable';
import BigNumber from 'bignumber.js';
import { toast } from 'react-toastify';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { DATE_FORMAT } from 'util/ConstantValues';
import QrBarcodeScanner from 'components/atoms/QrBarcodeScanner';
import { BookingQRType } from 'util/Enums';

const RecycleQRScanning = () => {
  const { t } = useTranslation();
  const [scannedData, setScannedData] = useState<
    Record<
      string,
      BookingQRScannedData & {
        qrCode: string;
      }
    >
  >({});
  const [scannedCode, setScannedCode] = useState<string>();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const { isLoading } = useSelector(selectLoading);

  const resources = useMemo(() => Object.values(scannedData), [scannedData]);

  const totalWeight = useMemo(
    () =>
      resources.reduce(
        (acc, item) => BigNumber(item.weight).plus(acc),
        BigNumber(0)
      ),
    [resources]
  );

  const totalSelectedWeight = useMemo(
    () =>
      resources.reduce(
        (acc, item) =>
          checkedIds.includes(item.qrCode)
            ? BigNumber(item.weight).plus(acc)
            : acc,
        BigNumber(0)
      ),
    [resources, checkedIds]
  );

  const handleScanSuccess = useCallback((decodedText: string) => {
    if (decodedText) {
      setScannedCode(decodedText);
    } else {
      setScannedCode(undefined);
    }
  }, []);

  const getScannedData = async () => {
    if (!scannedCode || isLoading || scannedData[scannedCode]) {
      return;
    }

    try {
      const { data } = await createAxios().get<BookingQRScannedData>(
        getApiPath(API_ENDPOINTS.RECYCLE_QR_SCANNING, {
          qrCode: scannedCode,
        })
      );

      const getIdentifier = (
        d: BookingQRScannedData & {
          qrCode: string;
        }
      ) => {
        if (d.type === BookingQRType.Emission) {
          return d.deliveryId || d.processingObjectId;
        }

        if (d.type === BookingQRType.General) {
          return d.qrCode;
        }

        return d.processingObjectId;
      };

      if (
        !Object.values(scannedData).some(
          (item) =>
            getIdentifier(item) ===
            getIdentifier({ ...data, qrCode: scannedCode })
        )
      ) {
        setScannedData((prev) => ({
          ...prev,
          [scannedCode]: {
            ...data,
            qrCode: scannedCode,
          },
        }));
        setCheckedIds((prev) => [...prev, scannedCode]);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setScannedCode(undefined);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!checkedIds.length) return;

      const selectedResources = resources.filter((item) =>
        checkedIds.includes(item.qrCode)
      );

      const selectedMaterials = selectedResources.map(
        ({ materialName }) => materialName
      );

      if (!selectedResources.length) return;

      if (
        selectedMaterials.some((material) => material !== selectedMaterials[0])
      ) {
        toast.error(t('collect_registration.submit_error_material_name'));

        return;
      }

      const registeringEmissionDeliverIds: number[] = [];
      const registeringEmissionIds: string[] = [];
      const registeringGeneralQrCodes: string[] = [];
      const registeringResourceIds: string[] = [];

      selectedResources.forEach(
        ({ deliveryId, processingObjectId, type, qrCode }) => {
          if (type === BookingQRType.Emission) {
            if (deliveryId) {
              registeringEmissionDeliverIds.push(deliveryId);
            } else {
              registeringEmissionIds.push(nonNull(processingObjectId));
            }
          } else if (type === BookingQRType.General) {
            registeringGeneralQrCodes.push(qrCode);
          } else {
            registeringResourceIds.push(nonNull(processingObjectId));
          }
        }
      );

      const promises: Promise<void>[] = [];

      if (
        registeringEmissionDeliverIds.length ||
        registeringEmissionIds.length
      ) {
        promises.push(
          createAxios().post(API_ENDPOINTS.RECYCLE_RECEIVE_EMISSIONS, {
            deliveryIds: registeringEmissionDeliverIds,
            emissionIds: registeringEmissionIds,
          })
        );
      }

      if (registeringGeneralQrCodes.length) {
        promises.push(
          createAxios({
            prefix: 'v2',
          }).post(API_ENDPOINTS.RECYCLE_REGIST_TEMP_RECEIVE, {
            qrCodes: registeringGeneralQrCodes,
          })
        );
      }

      if (registeringResourceIds.length) {
        promises.push(
          createAxios().post(API_ENDPOINTS.RECYCLE_RECEIVES, {
            deliveryIds: [],
            resourceIds: registeringResourceIds,
          })
        );
      }

      await Promise.all(promises);

      toast.success(t('messages.M_135'));
      setScannedData((prev) => {
        const newData = { ...prev };
        selectedResources.forEach(({ qrCode }) => {
          delete newData[qrCode];
        });

        return newData;
      });
      setCheckedIds([]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message
          ? e.response.data.message
          : t('receives.signup_messages.failed')
      );
    }
  };

  useEffect(() => {
    getScannedData();
  }, [scannedCode]);

  return (
    <div className={styles.scanWrapper}>
      <div className={styles.content}>
        <QrBarcodeScanner
          onScanSuccess={handleScanSuccess}
          classNames={{ container: styles.qrCodeRegion, video: styles.scanner }}
        />

        <div className={styles.scannedContent}>
          <div className={styles.scannedContentHeader}>
            <p className={styles.scannedTitle}>
              {t('qr_scanning.scanned_result')}
            </p>
            <p className={styles.scannedInfo}>
              {t('qr_scanning.scanned_total_items', {
                total: normalizeNumber({
                  value: resources.length.toString(),
                }),
              })}
            </p>
            <p className={styles.scannedInfo}>
              {t('qr_scanning.scanned_total_weight', {
                weight: normalizeNumber({ value: totalWeight }),
              })}
            </p>
          </div>

          <div className={styles.scannedTableWrapper}>
            <BookingScannedTable
              data={scannedData}
              checkedIds={checkedIds}
              setCheckedIds={setCheckedIds}
              columns={[
                {
                  title: t('qr_scanning.material'),
                  dataIndex: 'materialName',
                  render: ({ materialName }) => materialName,
                  width: '25%',
                },
                {
                  title: t('qr_scanning.id'),
                  dataIndex: 'deliveryId',
                  render: ({ deliveryId, processingObjectId }) =>
                    processingObjectId || deliveryId,
                  width: '30%',
                },
                {
                  title: t('qr_scanning.date_and_time'),
                  dataIndex: 'requestedDatetime',
                  width: '25%',
                  render: ({ requestedDatetime }) =>
                    getFormatDateWithCurrentTimezone(
                      requestedDatetime,
                      DATE_FORMAT.slaYMDHm
                    ),
                },
                {
                  title: t('qr_scanning.weight'),
                  dataIndex: 'weight',
                  width: '15%',
                  render: ({ weight }) =>
                    `${normalizeNumber({ value: weight })}kg`,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className={styles.scannedFooter}>
        <div className={styles.scannedFooter}>
          <p>
            {t('qr_scanning.total_selected_weight', {
              weight: normalizeNumber({
                value: totalSelectedWeight,
              }),
            })}
          </p>
        </div>

        <button
          className={styles.btnRequest}
          type="button"
          disabled={!checkedIds.length}
          onClick={handleSubmit}
        >
          {t('common.receive')}
        </button>
      </div>
    </div>
  );
};

export default RecycleQRScanning;
